import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc,
  getDocs, 
  query, 
  where, 
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface FormSubmission {
  id: string;
  templateId: string;           // Firebase field: templateId
  templateName?: string;        // Firebase field: templateName
  organizationId: string;       // Firebase field: organizationId
  submittedBy: string;          // Firebase field: submittedBy (user ID)
  submittedByName: string;      // Firebase field: submittedByName (display name)
  status: 'draft' | 'submitted' | 'approved' | 'rejected'; // Firebase field: status
  data: Record<string, any>;    // Firebase field: data (form field values)
  createdAt: Date;             // Firebase field: createdAt (timestamp)
  updatedAt: Date;             // Firebase field: updatedAt (timestamp)
  metadata?: {                 // Firebase field: metadata (optional)
    source?: string;           // e.g., 'form_filler', 'api', 'import'
    userAgent?: string;        // Browser user agent
    ipAddress?: string;        // User IP address
    referrer?: string;         // Referrer URL
  };
}

export interface FormSubmissionQuery {
  formTemplateId: string;       // Filter by templateId field
  organizationId?: string;      // Filter by organizationId field
  status?: string;              // Filter by status field
  submittedBy?: string;         // Filter by submittedBy field
  pageSize?: number;            // Number of items per page (default: 25)
  lastDoc?: DocumentSnapshot;   // Last document for pagination
  sortField?: string;           // Sort by field (default: createdAt)
  sortDirection?: 'asc' | 'desc'; // Sort direction (default: desc)
  searchTerm?: string;          // Search term for filtering
  filters?: Record<string, any>; // Additional filters
}

const FORM_SUBMISSIONS_COLLECTION = 'formSubmissions';

export class FormSubmissionService {
  // Helper function to safely convert Firestore timestamp to Date
  private static convertTimestamp(timestamp: any): Date {
    if (!timestamp) return new Date();
    
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    try {
      return new Date(timestamp);
    } catch {
      return new Date();
    }
  }

  // Submit a form
  static async submitForm(submission: Omit<FormSubmission, 'id' | 'createdAt' | 'updatedAt'>): Promise<FormSubmission> {
    try {
      const createdAt = Timestamp.now();
      const updatedAt = Timestamp.now();
      
      const submissionData = {
        ...submission,
        status: submission.status || 'submitted',
        createdAt,
        updatedAt
      };

      const docRef = await addDoc(collection(db, FORM_SUBMISSIONS_COLLECTION), submissionData);
      
      return {
        ...submissionData,
        id: docRef.id,
        createdAt: createdAt.toDate(),
        updatedAt: updatedAt.toDate()
      } as FormSubmission;
    } catch (error) {
      console.error('Error submitting form:', error);
      throw new Error('Failed to submit form');
    }
  }


  // Get form submissions with pagination and filtering
  static async getFormSubmissions(queryParams: FormSubmissionQuery): Promise<{
    submissions: FormSubmission[];
    hasMore: boolean;
    lastDoc?: DocumentSnapshot;
  }> {
    try {
      const {
        formTemplateId,
        organizationId,
        status,
        submittedBy,
        pageSize = 25,
        lastDoc,
        sortField = 'submittedAt',
        sortDirection = 'desc'
      } = queryParams;

      let q = query(
        collection(db, FORM_SUBMISSIONS_COLLECTION),
        where('templateId', '==', formTemplateId)
      );

      // Add organization filter if provided
      if (organizationId) {
        q = query(q, where('organizationId', '==', organizationId));
      }

      // Add status filter if provided
      if (status) {
        q = query(q, where('status', '==', status));
      }

      // Add submittedBy filter if provided
      if (submittedBy) {
        q = query(q, where('submittedBy', '==', submittedBy));
      }

      // Add sorting - use createdAt as default instead of submittedAt
      const actualSortField = sortField === 'submittedAt' ? 'createdAt' : sortField;
      q = query(q, orderBy(actualSortField, sortDirection));

      // Add pagination
      q = query(q, limit(pageSize + 1)); // Get one extra to check if there are more

      // Add cursor for pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;
      
      // Check if there are more results
      const hasMore = docs.length > pageSize;
      const submissions = docs.slice(0, pageSize); // Remove the extra doc
      
      const formattedSubmissions = submissions.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          templateId: data.templateId,
          templateName: data.templateName,
          data: data.data || {},
          status: data.status || 'draft',
          submittedBy: data.submittedBy,
          submittedByName: data.submittedByName,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          organizationId: data.organizationId,
          metadata: data.metadata
        } as FormSubmission;
      });

      return {
        submissions: formattedSubmissions,
        hasMore,
        lastDoc: hasMore ? submissions[submissions.length - 1] : undefined
      };
    } catch (error) {
      console.error('Error getting form submissions:', error);
      throw new Error('Failed to get form submissions');
    }
  }

  // Get submission count for a form
  static async getSubmissionCount(formTemplateId: string, organizationId?: string): Promise<number> {
    try {
      let q = query(
        collection(db, FORM_SUBMISSIONS_COLLECTION),
        where('templateId', '==', formTemplateId)
      );

      if (organizationId) {
        q = query(q, where('organizationId', '==', organizationId));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting submission count:', error);
      return 0;
    }
  }

  // Create submission (alias for submitForm for backward compatibility)
  static async createSubmission(submission: Omit<FormSubmission, 'id' | 'createdAt' | 'updatedAt'>): Promise<FormSubmission> {
    return this.submitForm(submission);
  }

  // Get all submissions for organization
  static async getSubmissions(params: { organizationId?: string }): Promise<FormSubmission[]> {
    try {
      let q = query(collection(db, FORM_SUBMISSIONS_COLLECTION));

      if (params.organizationId) {
        q = query(q, where('organizationId', '==', params.organizationId));
      }

      q = query(q, orderBy('createdAt', 'desc'));

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          templateId: data.templateId,
          templateName: data.templateName,
          data: data.data || {},
          status: data.status || 'draft',
          submittedBy: data.submittedBy,
          submittedByName: data.submittedByName,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          organizationId: data.organizationId,
          metadata: data.metadata
        } as FormSubmission;
      });
    } catch (error) {
      console.error('Error getting submissions:', error);
      throw new Error('Failed to get submissions');
    }
  }

  // Get user's submissions
  static async getUserSubmissions(userId: string, organizationId?: string): Promise<FormSubmission[]> {
    try {
      let q = query(
        collection(db, FORM_SUBMISSIONS_COLLECTION),
        where('submittedBy', '==', userId)
      );

      if (organizationId) {
        q = query(q, where('organizationId', '==', organizationId));
      }

      q = query(q, orderBy('createdAt', 'desc'));

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          templateId: data.templateId,
          templateName: data.templateName,
          data: data.data || {},
          status: data.status || 'draft',
          submittedBy: data.submittedBy,
          submittedByName: data.submittedByName,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          organizationId: data.organizationId,
          metadata: data.metadata
        } as FormSubmission;
      });
    } catch (error) {
      console.error('Error getting user submissions:', error);
      throw new Error('Failed to get user submissions');
    }
  }

  // Approve submission
  static async approveSubmission(submissionId: string, reviewedBy: string, notes?: string): Promise<void> {
    try {
      const submissionRef = doc(db, FORM_SUBMISSIONS_COLLECTION, submissionId);
      await updateDoc(submissionRef, {
        status: 'approved',
        reviewedBy,
        reviewedAt: Timestamp.now(),
        reviewNotes: notes || '',
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error approving submission:', error);
      throw new Error('Failed to approve submission');
    }
  }

  // Reject submission
  static async rejectSubmission(submissionId: string, reviewedBy: string, notes?: string): Promise<void> {
    try {
      const submissionRef = doc(db, FORM_SUBMISSIONS_COLLECTION, submissionId);
      await updateDoc(submissionRef, {
        status: 'rejected',
        reviewedBy,
        reviewedAt: Timestamp.now(),
        reviewNotes: notes || '',
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error rejecting submission:', error);
      throw new Error('Failed to reject submission');
    }
  }
}